import React, { useState, useRef } from 'react';
import axios from 'axios';
import { X, Upload, FileSpreadsheet, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportExcelModal = ({ collection, onClose, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/${collection}/import-excel`, formData);
      setResult(res.data.imported);
      toast.success(`${res.data.imported} registros importados`);
      onImported();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    }
    setImporting(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/${collection}/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_${collection}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch {
      toast.error('Error al descargar plantilla');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Importar {collection.charAt(0).toUpperCase() + collection.slice(1)}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {result !== null ? (
          <div className="text-center py-8">
            <CheckCircle size={48} className="text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-gray-900">{result} registros importados</p>
            <button onClick={onClose} className="mt-4 px-6 py-2.5 rounded-xl bg-[#467322] text-white font-bold text-sm hover:bg-[#3A601C] transition-colors">
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <button onClick={downloadTemplate}
              className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 transition-colors mb-4"
              data-testid="download-template-btn">
              <FileSpreadsheet size={20} className="text-emerald-600" />
              <div className="text-left">
                <p className="text-sm font-bold text-emerald-800">Descargar Plantilla Excel</p>
                <p className="text-xs text-emerald-600">Usa este archivo como guia para tus datos</p>
              </div>
            </button>

            <div
              onClick={() => fileRef.current?.click()}
              className={`w-full p-8 rounded-xl border-2 border-dashed transition-colors cursor-pointer text-center ${
                file ? 'border-[#467322] bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'
              }`}
              data-testid="upload-zone">
              {file ? (
                <>
                  <FileSpreadsheet size={32} className="text-[#467322] mx-auto mb-2" />
                  <p className="text-sm font-bold text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                </>
              ) : (
                <>
                  <Upload size={32} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-gray-600">Haz clic para seleccionar archivo Excel</p>
                  <p className="text-xs text-gray-400 mt-1">.xlsx</p>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile} className="hidden" />

            <div className="flex gap-3 mt-5">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleImport} disabled={!file || importing}
                className="flex-1 py-2.5 rounded-xl bg-[#467322] text-white font-bold text-sm hover:bg-[#3A601C] transition-colors disabled:opacity-50"
                data-testid="import-submit-btn">
                {importing ? <Loader2 size={16} className="inline animate-spin mr-1" /> : null}
                Importar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportExcelModal;
